import { Component, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

import { LucideAngularModule } from 'lucide-angular';

import { SidebarComponent } from './components/sidebar/sidebar.component';
import { MobileNavigationComponent } from './components/mobile-navigation/mobile-navigation.component';
import { TopBarComponent } from './components/topbar/topbar.component';
import { CommandPaletteComponent } from '../../shared/components/command-palette/command-palette.component';
import { RoutesSalesEnum } from '../../modules/sales/enums/routes-sales.enum';
import { PermissionsService } from '../services';
import { OnboardingWelcomeComponent } from '../onboarding/components/onboarding-welcome/onboarding-welcome.component';
import { GuidedTourHostComponent } from '../onboarding/components/guided-tour-host/guided-tour-host.component';
import { AuthFacade } from '../../modules/auth/facades';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    SidebarComponent,
    MobileNavigationComponent,
    TopBarComponent,
    RouterModule,
    LucideAngularModule,
    CommandPaletteComponent,
    GuidedTourHostComponent,
    OnboardingWelcomeComponent
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  host: {
    '(window:keydown)': 'handleGlobalNewSaleShortcut($event)'
  }
})
export class LayoutComponent {
  private readonly authFacade = inject(AuthFacade);
  private readonly router = inject(Router);
  protected readonly permissions = inject(PermissionsService);
  protected readonly currentRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0])
    ),
    { initialValue: this.router.url.split('?')[0] }
  );

  protected readonly newSaleRoute = RoutesSalesEnum.CREATE;
  private readonly commercialAccess = typeof this.authFacade?.commercialAccess === 'function'
    ? this.authFacade.commercialAccess
    : toSignal(this.authFacade.commercialAccess$, { initialValue: null });
  protected readonly isTrialExpired = computed(
    () => this.commercialAccess()?.status === 'trial_expired'
  );

  protected handleGlobalNewSaleShortcut(event: KeyboardEvent): void {
    if (event.key !== 'F2' || event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return;

    event.preventDefault();

    if (this.isTrialExpired() || !this.permissions.can('sales', 'create') || this.currentRoute() === RoutesSalesEnum.CREATE) return;

    void this.router.navigate([this.newSaleRoute]);
  }
}
